import React, { useEffect, useState } from "react";
import { FaSmile, FaMeh, FaFrown, FaSpinner, FaSave, FaTimes, FaCog, FaPlus, FaTrash, FaDownload, FaTable, FaEdit, FaCheck } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useUsers } from "../../context/UsersContext";
import { useNotas } from "../../context/NotasContext";
import { obtenerAsignaturasSalon } from "../../services/notasService"; // ✅ AGREGAR ESTE IMPORT
import DropDown from "../DropDown";

const NotasForm = ({
  students,
  periodos,
  asignaturas,
  createNotaData,
  updateNotaData,
  salonGrado,
  editingNota,
  clearEditing,
  showNotification
}) => {
  const { user } = useAuth();
  const { salones, fetchSalones } = useUsers();
  const { 
    planillaConMotivos,
    loadingPlanilla,
    asignaturasSalon,
    loadingAsignaturas,
    getAsignaturasSalon,
    cargarPlanillaConMotivos,
    agregarMotivoData,
    editarMotivoData,
    eliminarMotivoData,
    guardarCalificacionesMotivoData
  } = useNotas();

  // Estados principales
  const [selectedSalon, setSelectedSalon] = useState("");
  const [selectedPeriodo, setSelectedPeriodo] = useState("");
  const [selectedAsignatura, setSelectedAsignatura] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Estados para columnas dinámicas (motivos)
  const [motivosNotas, setMotivosNotas] = useState([]);
  const [nuevoMotivo, setNuevoMotivo] = useState("");
  const [showModalMotivos, setShowModalMotivos] = useState(false);
  const [planillaData, setPlanillaData] = useState({});
  const [cambiosPendientes, setCambiosPendientes] = useState({});


  // ✅ NUEVO: Estados para edición de motivos
  const [editandoMotivo, setEditandoMotivo] = useState(null);
  const [nombreMotivoEdit, setNombreMotivoEdit] = useState("");
  
  // Estados para edición inline
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [celdaActiva, setCeldaActiva] = useState(null);

  const [pendingClose, setPendingClose] = useState(null);

  // NUEVO: Cargar salones según el rol
  useEffect(() => {
    if (user?.rol_id === 1) {
      // Admin: cargar todos los salones
      fetchSalones();
    } else if (user?.rol_id === 2 && salonGrado) {
      // Profesor: auto-seleccionar su salón
      setSelectedSalon(salonGrado.salon_id?.toString() || "");
    }
  }, [user, fetchSalones, salonGrado]);

  useEffect(() => {
    if (pendingClose) {
      // ✅ PRIMERO guardar el valor en planillaData
      if (editingCell) {
        const { studentId, motivoId } = editingCell;
        const key = `${studentId}-${motivoId}`;
        
        // Guardar el valor seleccionado
        setPlanillaData(prev => ({ ...prev, [key]: editValue }));
        setCambiosPendientes(prev => ({
          ...prev,
          [key]: {
            motivo_id: parseInt(motivoId),
            students_id: parseInt(studentId),
            calificacion: esPreescolar ? null : (editValue === "" ? null : parseFloat(editValue)),
            simbolo_carita: esPreescolar ? (editValue || null) : null
          }
        }));
      }
      
      // ✅ LUEGO limpiar el estado de edición
      setEditingCell(null);
      setEditValue("");
      
      // ✅ FINALMENTE intentar mover a la siguiente celda si existe
      if (pendingClose.moveNext && celdaActiva) {
        const { studentIndex, motivoIndex } = celdaActiva;
        const estudiantes = planillaConMotivos.estudiantes || [];
        
        if (motivoIndex < motivosNotas.length - 1) {
          // Hay más motivos en la misma fila
          const nextMotivo = motivosNotas[motivoIndex + 1];
          const student = estudiantes[studentIndex];
          setTimeout(() => {
            startEdit(student.students_id, nextMotivo.id, null, studentIndex, motivoIndex + 1);
          }, 50);
        } else if (studentIndex < estudiantes.length - 1) {
          // Hay más estudiantes
          const nextStudent = estudiantes[studentIndex + 1];
          const firstMotivo = motivosNotas[0];
          setTimeout(() => {
            startEdit(nextStudent.students_id, firstMotivo.id, null, studentIndex + 1, 0);
          }, 50);
        } else {
          // ✅ Es el último estudiante y último motivo - solo limpiar celda activa
          setCeldaActiva(null);
        }
      } else {
        setCeldaActiva(null);
      }
      
      setPendingClose(null);
    }
  }, [pendingClose]);

  // ✅ MODIFICAR: Cargar asignaturas cuando se seleccione salón
  useEffect(() => {
    const cargarAsignaturas = async () => {
      const salonId = user?.rol_id === 2 ? (salonGrado?.salon_id || selectedSalon) : selectedSalon;
      
      if (!salonId) {
        return;
      }

      try {
        await getAsignaturasSalon(salonId);
      } catch (error) {
        console.error("Error cargando asignaturas:", error);
      }
    };

    cargarAsignaturas();
  }, [selectedSalon, salonGrado, user]);

  // Obtener grado del salón seleccionado
  const getSalonGrado = () => {
    if (user?.rol_id === 2 && salonGrado) {
      return salonGrado.salon_grado?.toLowerCase();
    }
    if (user?.rol_id === 1 && selectedSalon) {
      const salon = salones.find(s => s.id === parseInt(selectedSalon));
      return salon?.grado?.toLowerCase();
    }
    return null;
  };

  const gradoActual = getSalonGrado();
  const esPreescolar = ["parvulos", "prejardin", "jardin", "transicion"].includes(gradoActual)

  // CARGAR PLANILLA CON MOTIVOS
  const handleCargarPlanilla = async () => {
    // CORREGIDO: Validar que haya salón seleccionado
    const salonId = user?.rol_id === 2 ? (salonGrado?.salon_id || selectedSalon) : selectedSalon;
    
    if (!salonId || !selectedPeriodo || !selectedAsignatura) {
      showNotification({
        type: "error",
        message: "Debe seleccionar periodo y asignatura"
      });
      return;
    }

    try {
      const response = await cargarPlanillaConMotivos(salonId, selectedPeriodo, selectedAsignatura);
      
      setMotivosNotas(response.data.motivos || []);
      
      const dataInicial = {};
      response.data.estudiantes.forEach(est => {
        Object.entries(est.notas).forEach(([motivoId, nota]) => {
          const key = `${est.students_id}-${motivoId}`;
          dataInicial[key] = nota.simbolo_carita || nota.calificacion || "";
        });
      });
      
      setPlanillaData(dataInicial);
      setCambiosPendientes({});
      
      showNotification({ 
        type: "success", 
        message: `Planilla cargada: ${response.data.estudiantes.length} estudiantes, ${response.data.motivos.length} columnas` 
      });
    } catch (error) {
      showNotification({
        type: "error",
        message: error.response?.data?.message || "Error al cargar la planilla"
      });
    }
  };

  // AGREGAR MOTIVO
  const handleAgregarMotivo = async () => {
    if (!nuevoMotivo.trim()) {
      showNotification({ type: "error", message: "El nombre del motivo no puede estar vacío." });
      return;
    }

    // 🔥 NUEVO: Limitar a 1 columna para preescolar
    if (esPreescolar && motivosNotas.length >= 1) {
      showNotification({
        type: "error",
        message: "En preescolar solo se permite una columna de calificación"
      });
      return;
    }

    // CORREGIDO: Obtener salon_id según el rol
    const salonId = user?.rol_id === 2 ? (salonGrado?.salon_id || selectedSalon) : selectedSalon;

    if (!salonId || !selectedPeriodo || !selectedAsignatura) {
      showNotification({ type: "error", message: "Debe seleccionar periodo y asignatura primero." });
      return;
    }

    try {
      await agregarMotivoData({
        salon_id: parseInt(salonId),
        periodo_id: parseInt(selectedPeriodo),
        asignatura_salon_id: parseInt(selectedAsignatura),
        nombre: nuevoMotivo
      });

      
      setNuevoMotivo("");
      showNotification({ type: "success", message: "Columna agregada correctamente." });

      await handleCargarPlanilla();
    } catch (error) {
      showNotification({ 
        type: "error", 
        message: error.response?.data?.message || "Error al agregar columna" 
      });
    }
  };

  // ✅ NUEVO: Función para iniciar edición de motivo
  const handleEditarMotivo = (motivo) => {
    setEditandoMotivo(motivo.id);
    setNombreMotivoEdit(motivo.nombre);
  };

  // ✅ NUEVO: Función para guardar motivo editado
  const handleGuardarEdicionMotivo = async () => {
    if (!nombreMotivoEdit.trim()) {
      showNotification({
        type: "error",
        message: "El nombre no puede estar vacío"
      });
      return;
    }

    try {
      await editarMotivoData(editandoMotivo, nombreMotivoEdit);
      
      // Actualizar localmente
      setMotivosNotas(prev => 
        prev.map(m => m.id === editandoMotivo ? {...m, nombre: nombreMotivoEdit} : m)
      );
      
      setEditandoMotivo(null);
      setNombreMotivoEdit("");
      
      showNotification({
        type: "success",
        message: "Motivo actualizado correctamente"
      });
    } catch (error) {
      showNotification({
        type: "error",
        message: error.response?.data?.message || "Error al editar motivo"
      });
    }
  };

  // ELIMINAR MOTIVO
  const handleEliminarMotivo = async (motivoId) => {
    if (!window.confirm("¿Está seguro de eliminar esta columna? Se eliminarán todas las calificaciones asociadas.")) {
      return;
    }

    // CORREGIDO: Obtener salon_id según el rol
    const salonId = user?.rol_id === 2 ? (salonGrado?.salon_id || selectedSalon) : selectedSalon;

    try {
      await eliminarMotivoData(motivoId, salonId, selectedPeriodo, selectedAsignatura);
      
      await handleCargarPlanilla();
      
      showNotification({ type: "success", message: "Columna eliminada correctamente." });
    } catch (error) {
      showNotification({ 
        type: "error", 
        message: error.response?.data?.message || "Error al eliminar columna" 
      });
    }
  };

  // ...existing code... (startEdit, cancelEdit, confirmEdit, moveToNextCell, moveToPreviousCell, moveToCell igual)

  // INICIAR EDICIÓN DE CELDA
  const startEdit = (studentId, motivoId, currentValue = null, studentIndex = null, motivoIndex = null) => {
    if (editingCell) {
      confirmEdit(false);
    }
    
    const key = `${studentId}-${motivoId}`;
    const valor = currentValue !== null ? currentValue : (planillaData[key] || "");
    
    setEditingCell({ studentId, motivoId });
    setEditValue(valor);
    setCeldaActiva({ studentIndex, motivoIndex });
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
    setCeldaActiva(null);
  };

  const confirmEdit = (moveToNext = true) => {
    if (!editingCell) return;
    
    const { studentId, motivoId } = editingCell;
    const key = `${studentId}-${motivoId}`;
    
    if (esPreescolar) {
      if (!["feliz", "contento", "intermedio", "triste", ""].includes(editValue)) {
        cancelEdit();
        return;
      }
    } else {
      if (editValue !== "" && (isNaN(parseFloat(editValue)) || parseFloat(editValue) < 0 || parseFloat(editValue) > 5)) {
        showNotification({ type: "error", message: "La calificación debe estar entre 0 y 5" });
        cancelEdit();
        return;
      }
    }
    
    setPlanillaData(prev => ({ ...prev, [key]: editValue }));
    setCambiosPendientes(prev => ({
      ...prev,
      [key]: {
        motivo_id: parseInt(motivoId),
        students_id: parseInt(studentId),
        calificacion: esPreescolar ? null : (editValue === "" ? null : parseFloat(editValue)),
        simbolo_carita: esPreescolar ? (editValue || null) : null
      }
    }));
    
    setEditingCell(null);
    setEditValue("");
    
    if (moveToNext && celdaActiva) {
      moveToNextCell();
    } else {
      setCeldaActiva(null);
    }
  };

  const moveToNextCell = () => {
    if (!celdaActiva || motivosNotas.length === 0) return;
    const { studentIndex, motivoIndex } = celdaActiva;
    
    const estudiantes = planillaConMotivos.estudiantes || [];
    
    if (motivoIndex < motivosNotas.length - 1) {
      const nextMotivo = motivosNotas[motivoIndex + 1];
      const student = estudiantes[studentIndex];
      startEdit(student.students_id, nextMotivo.id, null, studentIndex, motivoIndex + 1);
    } 
    else if (studentIndex < estudiantes.length - 1) {
      const nextStudent = estudiantes[studentIndex + 1];
      const firstMotivo = motivosNotas[0];
      startEdit(nextStudent.students_id, firstMotivo.id, null, studentIndex + 1, 0);
    }
  };

  const moveToPreviousCell = () => {
    if (!celdaActiva || motivosNotas.length === 0) return;
    const { studentIndex, motivoIndex } = celdaActiva;
    
    const estudiantes = planillaConMotivos.estudiantes || [];
    
    if (motivoIndex > 0) {
      const prevMotivo = motivosNotas[motivoIndex - 1];
      const student = estudiantes[studentIndex];
      startEdit(student.students_id, prevMotivo.id, null, studentIndex, motivoIndex - 1);
    } 
    else if (studentIndex > 0) {
      const prevStudent = estudiantes[studentIndex - 1];
      const lastMotivo = motivosNotas[motivosNotas.length - 1];
      startEdit(prevStudent.students_id, lastMotivo.id, null, studentIndex - 1, motivosNotas.length - 1);
    }
  };

  const moveToCell = (direction) => {
    if (!celdaActiva || motivosNotas.length === 0) return;
    const { studentIndex, motivoIndex } = celdaActiva;
    
    const estudiantes = planillaConMotivos.estudiantes || [];
    
    switch(direction) {
      case 'up':
        if (studentIndex > 0) {
          const prevStudent = estudiantes[studentIndex - 1];
          const motivo = motivosNotas[motivoIndex];
          startEdit(prevStudent.students_id, motivo.id, null, studentIndex - 1, motivoIndex);
        }
        break;
      case 'down':
        if (studentIndex < estudiantes.length - 1) {
          const nextStudent = estudiantes[studentIndex + 1];
          const motivo = motivosNotas[motivoIndex];
          startEdit(nextStudent.students_id, motivo.id, null, studentIndex + 1, motivoIndex);
        }
        break;
    }
  };

  const renderCell = (student, motivo, studentIndex, motivoIndex) => {
    const key = `${student.students_id}-${motivo.id}`;
    const isEditing = editingCell?.studentId === student.students_id && editingCell?.motivoId === motivo.id;
    const currentValue = planillaData[key] || "";
    const tieneCambios = cambiosPendientes[key] !== undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmEdit(true);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        if (e.shiftKey) {
          confirmEdit(false);
          moveToPreviousCell();
        } else {
          confirmEdit(false);
          moveToNextCell();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        confirmEdit(false);
        moveToCell('up');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        confirmEdit(false);
        moveToCell('down');
      }
    };

    if (isEditing) {
      if (esPreescolar) {
        return (
          <div className="flex gap-1 justify-center flex-wrap p-2">
            {[
              { value: "feliz", icon: <FaSmile size={26} />, color: "text-green-light", bg: "bg-green-light bg-opacity-20" },
              { value: "contento", icon: <FaSmile size={26} />, color: "text-blue-500", bg: "bg-blue-100" },
              { value: "intermedio", icon: <FaMeh size={26} />, color: "text-yellow-500", bg: "bg-yellow-100" },
              { value: "triste", icon: <FaFrown size={26} />, color: "text-red-500", bg: "bg-red-100" },
            ].map(({ value, icon, color, bg }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setEditValue(value);
                  setPendingClose({ moveNext: true });
                }}
                className={`p-2 rounded-lg transition-all ${
                  editValue === value ? `${bg} ring-2 ring-current` : "hover:bg-gray-100"
                }`}
              >
                <span className={color}>{icon}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setEditValue("");
                setPendingClose({ moveNext: true });
              }}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
              title="Limpiar"
            >
              <FaTimes size={26} />
            </button>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center p-1">
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => confirmEdit(false)}
            className="w-full p-2 border-2 border-blue-400 rounded text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
            max="5"
            step="0.1"
            autoFocus
            placeholder="0-5"
          />
        </div>
      );
    }

    const displayValue = currentValue ? (
      esPreescolar ? (
        {
          "feliz": <FaSmile className="text-green-light text-xl mx-auto" />,
          "contento": <FaSmile className="text-blue-500 text-xl mx-auto" />,
          "intermedio": <FaMeh className="text-yellow-500 text-xl mx-auto" />,
          "triste": <FaFrown className="text-red-500 text-xl mx-auto" />
        }[currentValue] || currentValue
      ) : currentValue
    ) : "-";

    return (
      <div
        onClick={() => startEdit(student.students_id, motivo.id, null, studentIndex, motivoIndex)}
        className={`cursor-pointer hover:bg-blue-50 p-2 min-h-[40px] flex items-center justify-center transition ${
          tieneCambios ? "bg-yellow-100 font-semibold" : ""
        }`}
        title={tieneCambios ? "Cambio pendiente de guardar" : "Click para editar"}
      >
        {displayValue}
      </div>
    );
  };

  const handleGuardarTodas = async () => {
    const calificaciones = Object.values(cambiosPendientes);
    
    if (calificaciones.length === 0) {
      showNotification({ type: "info", message: "No hay cambios pendientes por guardar." });
      return;
    }

    setIsSaving(true);
    try {
      await guardarCalificacionesMotivoData(calificaciones);
      
      showNotification({ 
        type: "success", 
        message: `${calificaciones.length} calificaciones guardadas correctamente.` 
      });
      
      await handleCargarPlanilla();
      
    } catch (error) {
      showNotification({
        type: "error",
        message: error.response?.data?.message || "Error al guardar las calificaciones."
      });
    } finally {
      setIsSaving(false);
    }
  };

  const calcularPromedio = (estudiante) => {
    if (esPreescolar) return null;
    
    const notas = motivosNotas.map(motivo => {
      const key = `${estudiante.students_id}-${motivo.id}`;
      const valor = planillaData[key];
      return valor && !isNaN(parseFloat(valor)) ? parseFloat(valor) : null;
    }).filter(n => n !== null);
    
    if (notas.length === 0) return "-";
    
    const suma = notas.reduce((acc, nota) => acc + nota, 0);
    return (suma / notas.length).toFixed(1);
  };

  // NUEVO: Título dinámico según el rol
  const getTitulo = () => {
    if (user?.rol_id === 2) {
      return `Planilla de Notas - ${salonGrado?.salon_nombre || "Mi Salón"}`;
    }
    return "Planilla de Notas";
  };

  // NUEVO: Obtener nombre del salón para mostrar
  const getNombreSalon = () => {
    if (user?.rol_id === 2) {
      return salonGrado?.salon_nombre || "Mi Salón";
    }
    const salon = salones.find(s => s.id === parseInt(selectedSalon));
    return salon?.nombre || "";
  };

  // RENDERIZADO PRINCIPAL
  return (
    <div className="bg-white p-6 rounded-2xl shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FaTable className="text-blue-600" />
          {getTitulo()}
        </h3>
        
        {motivosNotas.length > 0 && (
          <button
            onClick={() => setShowModalMotivos(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition"
          >
            <FaCog size={16} />
            Gestionar Columnas ({motivosNotas.length})
          </button>
        )}
      </div>

      {/* FILTROS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* MODIFICADO: Solo mostrar selector de salón para admin */}
        {user?.rol_id === 1 && (
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Salón</label>
            <DropDown
              options={salones.map(s => ({ value: s.id, label: `${s.nombre} - ${s.grado}` }))}
              value={selectedSalon}
              onChange={(e) => {
                setSelectedSalon(e.target.value);
                setPlanillaData({});
                setCambiosPendientes({});
                setMotivosNotas([]);
              }}
              placeholder="Seleccione un salón"
            />
          </div>
        )}

        {/* NUEVO: Mostrar info del salón para profesores */}
        {user?.rol_id === 2 && (
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Salón Asignado</label>
            <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg font-medium text-blue-900">
              {salonGrado?.salon_nombre} - {salonGrado?.salon_grado}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">Periodo</label>
          <DropDown
            options={periodos
              .filter(p => p.nombre.toLowerCase() !== "periodo final") // 🔥 FILTRAR "Boletin Final"
              .map(p => ({ value: p.id, label: p.nombre }))}
            value={selectedPeriodo}
            onChange={(e) => {
              setSelectedPeriodo(e.target.value);
              setPlanillaData({});
              setCambiosPendientes({});
              setMotivosNotas([]);
            }}
            placeholder="Seleccione un periodo"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">Asignatura</label>
          <DropDown
            options={asignaturasSalon.map(a => ({ 
              value: a.asignatura_salon_id, // ✅ USAR asignatura_salon_id
              label: `${a.nombre}${a.IH ? ` (IH: ${a.IH})` : ''}` 
            }))}
            value={selectedAsignatura}
            onChange={(e) => {
              setSelectedAsignatura(e.target.value);
              setPlanillaData({});
              setCambiosPendientes({});
              setMotivosNotas([]);
            }}
            placeholder="Seleccione una asignatura"
            disabled={user?.rol_id === 1 && !selectedSalon}
          />
        </div>
      </div>

      {/* ...existing code... (resto del componente igual) */}

      {/* BOTONES DE ACCIÓN */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <button
          onClick={handleCargarPlanilla}
          disabled={!selectedPeriodo || !selectedAsignatura || loadingPlanilla}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
        >
          {loadingPlanilla ? (
            <>
              <FaSpinner className="animate-spin" />
              Cargando...
            </>
          ) : (
            <>
              <FaDownload />
              Cargar Planilla
            </>
          )}
        </button>

        {motivosNotas.length === 0 && selectedPeriodo && selectedAsignatura && !loadingPlanilla && (
          <button
            onClick={() => setShowModalMotivos(true)}
            className="flex items-center gap-2 bg-green text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
          >
            <FaPlus />
            Agregar Primera Columna
          </button>
        )}

        {Object.keys(cambiosPendientes).length > 0 && (
          <button
            onClick={handleGuardarTodas}
            disabled={isSaving}
            className="flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed ml-auto transition"
          >
            {isSaving ? (
              <>
                <FaSpinner className="animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <FaSave />
                Guardar Cambios ({Object.keys(cambiosPendientes).length})
              </>
            )}
          </button>
        )}
      </div>

      {/* LOADING */}
      {loadingPlanilla && (
        <div className="text-center py-12">
          <FaSpinner className="animate-spin text-5xl text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando planilla...</p>
        </div>
      )}

      {/* TABLA ESTILO EXCEL */}
      {!loadingPlanilla && planillaConMotivos.estudiantes && planillaConMotivos.estudiantes.length > 0 && motivosNotas.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <th className="border border-gray-300 px-4 py-3 font-semibold sticky left-0 bg-blue-700 z-20 min-w-[200px]">
                    Estudiante
                  </th>
                  {motivosNotas.map((motivo) => (
                    <th
                      key={motivo.id}
                      className="border border-gray-300 px-3 py-3 font-medium text-xs min-w-[120px]"
                    >
                      <div className="truncate" title={motivo.nombre}>
                        {motivo.nombre}
                      </div>
                    </th>
                  ))}
                  {!esPreescolar && (
                    <th className="border border-gray-300 px-4 py-3 font-semibold bg-yellow-500 min-w-[100px]">
                      Promedio
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {planillaConMotivos.estudiantes.map((student, studentIndex) => (
                  <tr 
                    key={student.students_id} 
                    className={studentIndex % 2 === 0 ? "bg-white hover:bg-blue-50" : "bg-gray-50 hover:bg-blue-50"}
                  >
                    <td className="border border-gray-300 px-4 py-2 font-medium sticky left-0 bg-inherit z-10">
                      <div className="truncate">
                        {`${student.first_name} ${student.second_name || ""} ${student.first_surname} ${student.second_surname || ""}`.trim()}
                      </div>
                    </td>
                    {motivosNotas.map((motivo, motivoIndex) => (
                      <td
                        key={motivo.id}
                        className="border border-gray-300 p-0 text-center"
                      >
                        {renderCell(student, motivo, studentIndex, motivoIndex)}
                      </td>
                    ))}
                    {!esPreescolar && (
                      <td className="border border-gray-300 px-3 py-2 text-center font-bold bg-yellow-50">
                        {calcularPromedio(student)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* LEYENDA PARA PREESCOLAR */}
          {esPreescolar && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2 text-blue-900">Leyenda:</h4>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <FaSmile className="text-green-light" size={20} />
                  <span>Feliz - Excelente desempeño</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaSmile className="text-blue-500" size={20} />
                  <span>Contento - Buen desempeño</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaMeh className="text-yellow-500" size={20} />
                  <span>Intermedio - Desempeño aceptable</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaFrown className="text-red-500" size={20} />
                  <span>Triste - Necesita mejorar</span>
                </div>
              </div>
            </div>
          )}

          {/* TIPS DE USO */}
          <div className="mt-4 text-sm text-gray-600 bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
            <p className="font-semibold mb-2 text-purple-900">💡 Tips de uso:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Haz <strong>click</strong> en una celda para editarla</li>
              <li>Presiona <strong>Enter</strong> para guardar y avanzar a la siguiente celda</li>
              <li>Usa <strong>Tab</strong> para moverte a la derecha, <strong>Shift+Tab</strong> para la izquierda</li>
              <li>Usa las <strong>flechas ↑↓</strong> para navegar verticalmente</li>
              <li>Presiona <strong>Escape</strong> para cancelar la edición</li>
              <li>Las celdas con fondo <span className="bg-yellow-100 px-2 py-1 rounded">amarillo</span> tienen cambios sin guardar</li>
            </ul>
          </div>
        </>
      ) : !loadingPlanilla && selectedPeriodo && selectedAsignatura ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FaTable className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">
            {motivosNotas.length === 0 
              ? "No hay columnas definidas para esta configuración." 
              : "No hay estudiantes en este salón."}
          </p>
          {motivosNotas.length === 0 && (
            <button
              onClick={() => setShowModalMotivos(true)}
              className="px-6 py-3 bg-green text-white rounded-lg hover:bg-green-600 flex items-center gap-2 mx-auto"
            >
              <FaPlus />
              Agregar Primera Columna
            </button>
          )}
        </div>
      ) : !loadingPlanilla && (
        <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg">
          <FaTable className="text-6xl text-blue-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">
            {user?.rol_id === 2 
              ? "Selecciona periodo y asignatura para comenzar"
              : "Selecciona salón, periodo y asignatura para comenzar"}
          </p>
        </div>
      )}

      {/* MODAL DE GESTIÓN DE MOTIVOS */}
      {showModalMotivos && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                <FaCog className="text-purple-600" />
                Gestionar Columnas de Evaluación
              </h3>
              <button
                onClick={() => setShowModalMotivos(false)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <FaTimes size={24} />
              </button>
            </div>

            {!selectedPeriodo || !selectedAsignatura ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Debes seleccionar periodo y asignatura primero.</p>
                <button
                  onClick={() => setShowModalMotivos(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Entendido
                </button>
              </div>
            ) : (
              <>
                {/* INFO DE CONTEXTO */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-blue-900 mb-2">Configuración Actual:</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p><strong>Salón:</strong> {getNombreSalon()}</p>
                    <p><strong>Periodo:</strong> {periodos.find(p => p.id === parseInt(selectedPeriodo))?.nombre}</p>
                    <p>
                      <strong>Asignatura:</strong>{" "}
                      {asignaturasSalon.find(a => a.asignatura_salon_id === parseInt(selectedAsignatura))?.nombre || "No seleccionada"}
                    </p>
                  </div>
                </div>

                {/* AGREGAR NUEVO MOTIVO */}
                <div className="mb-6">
                  <label className="block mb-2 font-medium text-gray-700">Agregar Nueva Columna</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={nuevoMotivo}
                      onChange={(e) => setNuevoMotivo(e.target.value)}
                      placeholder="Ej: Trabajo Final, Quiz 1, Taller de Lectura..."
                      className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") handleAgregarMotivo();
                      }}
                    />
                    <button
                      onClick={handleAgregarMotivo}
                      disabled={!nuevoMotivo.trim()}
                      className="px-6 py-3 bg-green text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition"
                    >
                      <FaPlus size={14} />
                      Agregar
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Las columnas se guardan específicamente para este salón, periodo y asignatura.
                  </p>
                </div>

                {/* LISTA DE MOTIVOS EXISTENTES */}
                <div>
                  <h4 className="font-semibold mb-3 text-gray-800">Columnas Existentes</h4>
                  {motivosNotas.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <FaTable className="text-4xl text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">
                        No hay columnas definidas. Agrega una arriba para comenzar.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {motivosNotas.map((motivo, index) => (
                        <div
                          key={motivo.id}
                          className="flex justify-between items-center p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg hover:shadow-md transition"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <span className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                              {index + 1}
                            </span>
                            
                            {editandoMotivo === motivo.id ? (
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="text"
                                  value={nombreMotivoEdit}
                                  onChange={(e) => setNombreMotivoEdit(e.target.value)}
                                  className="flex-1 px-3 py-2 border-2 border-purple-400 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  autoFocus
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") handleGuardarEdicionMotivo();
                                  }}
                                />
                                <button
                                  onClick={handleGuardarEdicionMotivo}
                                  className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-1"
                                  title="Guardar"
                                >
                                  <FaCheck size={14} />
                                  <span className="text-xs">Guardar</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setEditandoMotivo(null);
                                    setNombreMotivoEdit("");
                                  }}
                                  className="p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                                  title="Cancelar"
                                >
                                  <FaTimes size={14} />
                                </button>
                              </div>
                            ) : (
                              <span className="font-medium text-gray-800 flex-1">{motivo.nombre}</span>
                            )}
                          </div>

                          {editandoMotivo !== motivo.id && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditarMotivo(motivo)}
                                className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition flex items-center gap-1"
                                title="Editar nombre"
                              >
                                <FaEdit size={14} />
                                <span className="text-xs">Editar</span>
                              </button>
                              <button
                                onClick={() => handleEliminarMotivo(motivo.id)}
                                className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-1"
                                title="Eliminar columna"
                              >
                                <FaTrash size={14} />
                                <span className="text-xs">Eliminar</span>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowModalMotivos(false)}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
                  >
                    Cerrar
                  </button>
                  {motivosNotas.length > 0 && (
                    <button
                      onClick={async () => {
                        setShowModalMotivos(false);
                        await handleCargarPlanilla();
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      Actualizar Planilla
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotasForm;